import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "@components/_Revamp/Modal";
import { TitleAndDescription } from "@components/_Revamp/TitleAndDescription/TitleAndDescription";
import Button from "@pages/EverGreen/Components/Button";
import { styled } from "@linaria/react";
import { isNull } from "lodash";
import { useHistory } from "react-router-dom";
import { ROUTE_PATH } from "@static/route-paths";
import { Text } from "@pages/EverGreen/Components/Text";
import { css } from "@linaria/core";
import { SCREEN_SIZE } from "@static/screen-size";
import { EverGreenStyle } from "@utils/Style/everGreen";
import { Checkbox } from "@components/_Shared/Checkbox";
import { themeDefault } from "src/theme-revamp";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import { updateTaxData } from "@redux/slices/EVER_GREEN/step3Slice";
import { trackOnFormStartOrSuccess, trackOnLoad } from "@pages/EverGreen/Helpers/analyticsHelper";

const selectContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 40px;
  max-height: 210px;
  overflow: scroll;
  & >div[data-id='others']{
    margin-top: -40px;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    overflow: unset;
    max-height: unset;
  }
`;

const otherReasonStyle = css`
  width: 100%;
  margin-top: 18px;
`;

const modalClass = css`
  .modal-content {
    max-width: 920px;
    max-height: 588px;
  }
  .modal-body{
    padding: 24px;
  }

  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    .modal-content {
    max-width: 920px;
    max-height: 788px
  }
  }
`;

const lengthTextStyle = css`
  text-align: right;
  margin-top: 8px;
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  line-height: 16.8px;
  letter-spacing: 0.015em;
  color: #c9c9c9;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 18px;
  flex-direction: column;

  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
  
  }
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -40px;
  margin-bottom: 24px;
  padding: "0 10px";
  font-style: normal;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 400;
  color: #8a8988;
  flex-direction: column;
  max-height: 408px;
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-weight: 600;
    max-height: unset;

  }
`;

const TextNormal = styled.div`
  color: #434343;
  margin: 0;
  letter-spacing: 0.04em;

  @media screen and (max-width: 588px) {
    line-spacing: 19.6px;
  }
`;

const TextArea = styled.textarea`
  box-sizing: border-box;
  resize: none;
  padding: 16px;
  border-radius: 8px;
  border: 1px ${EverGreenStyle.colors.grey[1000]} solid;
  height: 142px;
  width: 100%;
  box-shadow: none;
  outline: none;
  margin-top: 16px;
`;

interface ICRSModal {
  show: boolean;
  onCloseFn?: () => void;
  title?: string;
}

const titleCss = css`
  white-space: break-spaces;
  max-width: 260px;
  @media screen {
    max-width: unset;
  }
`;

const data = [
  {
    label:
      "I am currently working in country of residence for less than 6 months.",
    value: "lessThan6Months",
  },
  {
    label: "I am on culture/education exchange program.",
    value: "culture/educationExchange",
  },
  {
    label: "I travel regularly between jurisdiction for home and work.",
    value: "travelRegularly",
  },
  {
    label: "I am a student at an educational institution.",
    value: "student",
  },
  {
    label: "Others",
    value: "others",
  },
];

function CRSModal({ show, onCloseFn }: ICRSModal) {
  const history = useHistory();
  const isErrorModal = onCloseFn ? false : true;
  const [reason, setReason] = useState<string>();
  const [selected, setSelected] = useState<string>();
  const [error, setError] = useState<string>();
  const dispatch = useAvivaDispatch();
  const tax = useAvivaSelector((state) => state.everGreenStep3.tax);
  const othersTextArea = useRef<HTMLTextAreaElement>(null)

  const validateForm = useCallback(() => {
    if (!selected) {
      setError("invalidReason");
      return;
    } else {
      if (selected === "others") {
        if (!reason) {
          setError("invalidReason");
          return;
        }
      }
    }
    setError("");
  }, [reason, selected]);

  useEffect(() => {
    document.body.style.position = "fixed";
    return () => {
      document.body.style.position = "unset";
    };
  });

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  useEffect(() => {
    if (selected === undefined && tax.crsReason) {
      const selectedValue = data.find((d) => d.label === tax.crsReason)?.value;
      setSelected(selectedValue);
     
    }
  }, [selected, tax.crsReason]);

  useEffect(() => {
    if (reason === undefined && tax.crsReason === "Others") {
      setReason(tax.crsOtherReason || "");
        
    }
  }, [reason, selected, tax.crsOtherReason, tax.crsReason]);

  useEffect(()=>{
    if(othersTextArea.current !== null && selected==="others"){
      othersTextArea.current.scrollIntoView({behavior:'auto', block:'start'})
    }
  },[selected])

  // useEffect(()=>{
  //   if(show){
  //     trackOnLoad()
  //   }
  // },[show])

  return (
    <Modal show={show} className={modalClass}>
      <Text
        variant="title"
        align="left"
        margin={"0 0 60px 0"}
        textClass={titleCss}
      >
        Common Reporting Standard (CRS)
      </Text>

      <ContentContainer>
        <p>Your residency address, correspondence address or contact number is different from your country(ies) of tax residence, please select a reason that applies and submit relevant supporting documents:</p>
        <br />
        <div className={selectContainer}>
          {data.map((d) => {
            return (
              <Checkbox
                type="radio"
                theme={EverGreenStyle}
                checked={selected === d.value}
                newRedio={true}
                onChange={() => {
                  trackOnFormStartOrSuccess("DS_tax_declaration_crs_modal_form")

                  setSelected(d.value);
                  dispatch(updateTaxData({ crsReason: d.label }));
                  if (d.value === "others") {
                    dispatch(updateTaxData({ crsOtherReason: reason }));
                  } else {
                    dispatch(updateTaxData({ crsOtherReason: "" }));
                  }
                }}
                key={d.value}
              >
                <TextNormal>{d.label}</TextNormal>
              </Checkbox>
            );
          })}
          {selected === "others" ? (
          <div data-id="others" 
          >
            <p className={otherReasonStyle}>Provide more details below:</p>
            <TextArea
              ref={othersTextArea}

              placeholder=""
              onChange={(e) => {
                trackOnFormStartOrSuccess("DS_tax_declaration_crs_modal_form")

                setReason(e.target.value);
                dispatch(updateTaxData({ crsOtherReason: e.target.value }));
              }}
              value={reason}
              maxLength={200}
            />
            <p className={lengthTextStyle}>{reason?.length}/200</p>
          </div>
        ) : null}
        </div>
        
      </ContentContainer>
      <ButtonContainer>
        <Button
          variant="secondary"
          label="Back"
          width="180px"
          height="52px"
          onClick={() => {
            if (isErrorModal)
              history.push({
                pathname: ROUTE_PATH.EVER_GREEN_SINGPASS,
                state: { noBackButton: true },
              });
            else if (onCloseFn) onCloseFn();
          }}
        />
        <Button
          variant="default"
          label="Next"
          width="180px"
          height="52px"
          onClick={() => {
            trackOnFormStartOrSuccess("DS_tax_declaration_crs_modal_form", true)
            isErrorModal
              ? history.push({
                  pathname: ROUTE_PATH.EVER_GREEN_PEP,
                  state: { noBackButton: true },
                })
              : history.push(ROUTE_PATH.EVER_GREEN_PEP);
          }}
          disabled={Boolean(error)}
        />
      </ButtonContainer>
    </Modal>
  );
}

export default CRSModal;
